import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api/client';
import { auditLogKeys, contactKeys, invoiceKeys, paymentKeys } from '@/lib/queryKeys';
import type { PaymentRecord } from './usePayments';

export interface ReversePaymentInput {
  id: string;
  invoiceId?: string | null;
  contactId?: string | null;
  reason: string;
  kind: 'check_bounce' | 'ach_return' | 'manual';
}

interface ReversePaymentResponse {
  source: PaymentRecord;
  reversal: PaymentRecord;
}

export function useReversePayment() {
  const queryClient = useQueryClient();

  return useApiMutation<ReversePaymentResponse, ReversePaymentInput>({
    mutationFn: ({ id, reason, kind }) =>
      apiPost<ReversePaymentResponse, { reason: string; kind: ReversePaymentInput['kind'] }>(
        operationsClient,
        `/payments/${id}/reverse`,
        { reason, kind },
      ),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({ queryKey: paymentKeys.all() });
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all() });
      void queryClient.invalidateQueries({
        queryKey: auditLogKeys.byEntity('payment', variables.id),
      });
      if (variables.contactId) {
        void queryClient.invalidateQueries({
          queryKey: contactKeys.summary(variables.contactId),
        });
      }
      toast.success(
        variables.kind === 'check_bounce'
          ? 'Check marked as bounced'
          : variables.kind === 'ach_return'
            ? 'Payment marked as returned by bank'
            : 'Payment reversed',
      );
    },
  });
}
